'use client'

import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSwapHoriz,
    MdCheck,
    MdClose,
    MdPlayArrow,
    MdSearch,
    MdPerson,
    MdLocalHospital,
} from 'react-icons/md'
import {
    getTransfers,
    requestTransfer,
    authorizeTransfer,
    startTransfer,
    completeTransfer,
    cancelTransfer,
    BedTransfer,
    TRANSFER_STATUS_LABELS,
    TRANSFER_STATUS_COLORS,
    TRANSFER_TYPE_LABELS,
    TransferType,
} from '@/lib/api/hospital'
import {
    getActiveAdmissions,
    Admission,
} from '@/lib/api/hospital/admission-api'
import { RootState } from '@/store'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

export default function TransfersPage() {
    const company = useSelector((state: RootState) => state.auth.company)
    const companyId = company?.id || 0
    const {
        hospitals,
        loading: loadingHospitals,
        selectedHospitalId,
        setSelected,
    } = useHospitals()

    const [transfers, setTransfers] = useState<BedTransfer[]>([])
    const [loading, setLoading] = useState(true)
    const [showNewModal, setShowNewModal] = useState(false)
    const [newTransfer, setNewTransfer] = useState({
        admissionId: 0,
        type: 'INTERNAL' as TransferType,
        toUnitId: 0,
        toBedId: 0,
        reason: '',
        notes: '',
    })
    const [saving, setSaving] = useState(false)
    const [admissions, setAdmissions] = useState<Admission[]>([])
    const [loadingAdmissions, setLoadingAdmissions] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [showAdmissionDropdown, setShowAdmissionDropdown] = useState(false)

    useEffect(() => {
        loadTransfers()
    }, [selectedHospitalId])

    const loadAdmissions = async () => {
        if (!companyId) return

        setLoadingAdmissions(true)
        try {
            const response = await getActiveAdmissions(companyId)
            if (response.success) {
                setAdmissions(response.result || [])
            }
        } catch (error) {
            console.error('Erro ao carregar internações:', error)
        } finally {
            setLoadingAdmissions(false)
        }
    }

    const handleOpenModal = () => {
        setShowNewModal(true)
        setSearchTerm('')
        setShowAdmissionDropdown(false)
        if (companyId) {
            loadAdmissions()
        }
    }

    const handleCloseModal = () => {
        setShowNewModal(false)
        setNewTransfer({
            admissionId: 0,
            type: 'INTERNAL',
            toUnitId: 0,
            toBedId: 0,
            reason: '',
            notes: '',
        })
        setSearchTerm('')
        setShowAdmissionDropdown(false)
    }

    const filteredAdmissions = admissions.filter(admission => {
        if (!searchTerm) return true

        const search = searchTerm.toLowerCase()
        const patientName = admission.client?.name?.toLowerCase() || ''
        const unitName = admission.unit?.name?.toLowerCase() || ''
        const admissionId = String(admission.id)
        const admissionNumber = admission.admissionNumber?.toLowerCase() || ''

        return (
            patientName.includes(search) ||
            unitName.includes(search) ||
            admissionId.includes(search) ||
            admissionNumber.includes(search)
        )
    })

    const handleSelectAdmission = (admission: Admission) => {
        setNewTransfer(prev => ({
            ...prev,
            admissionId: admission.id,
        }))
        setSearchTerm(
            `${admission.client?.name || ''} - ${admission.unit?.name || ''} (ID: ${admission.id})`
        )
        setShowAdmissionDropdown(false)
    }

    const loadTransfers = async () => {
        setLoading(true)
        try {
            const response = await getTransfers(undefined, {
                hospitalId: selectedHospitalId || undefined,
            })
            if (response.success) {
                setTransfers(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar transferências')
            }
        } catch (error) {
            toast.error('Erro ao carregar transferências')
        } finally {
            setLoading(false)
        }
    }

    const handleRequestTransfer = async () => {
        if (!newTransfer.admissionId || !newTransfer.reason) {
            toast.error('Preencha os campos obrigatórios')
            return
        }

        setSaving(true)
        try {
            const response = await requestTransfer({
                ...newTransfer,
                toUnitId: newTransfer.toUnitId || undefined,
                toBedId: newTransfer.toBedId || undefined,
                notes: newTransfer.notes || undefined,
            })
            if (response.success) {
                toast.success('Transferência solicitada com sucesso')
                handleCloseModal()
                loadTransfers()
            } else {
                toast.error(response.error || 'Erro ao solicitar transferência')
            }
        } catch (error) {
            toast.error('Erro ao solicitar transferência')
        } finally {
            setSaving(false)
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            if (!target.closest('.relative') && showAdmissionDropdown) {
                setShowAdmissionDropdown(false)
            }
        }

        if (showAdmissionDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
            return () =>
                document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showAdmissionDropdown])

    const handleAuthorize = async (id: number, approved: boolean) => {
        try {
            const response = await authorizeTransfer(id, { approved })
            if (response.success) {
                toast.success(
                    approved
                        ? 'Transferência autorizada'
                        : 'Transferência negada'
                )
                loadTransfers()
            } else {
                toast.error(response.error || 'Erro ao autorizar transferência')
            }
        } catch (error) {
            toast.error('Erro ao autorizar transferência')
        }
    }

    const handleStart = async (id: number) => {
        try {
            const response = await startTransfer(id)
            if (response.success) {
                toast.success('Transferência iniciada')
                loadTransfers()
            } else {
                toast.error(response.error || 'Erro ao iniciar transferência')
            }
        } catch (error) {
            toast.error('Erro ao iniciar transferência')
        }
    }

    const handleComplete = async (id: number) => {
        try {
            const response = await completeTransfer(id)
            if (response.success) {
                toast.success('Transferência concluída')
                loadTransfers()
            } else {
                toast.error(response.error || 'Erro ao concluir transferência')
            }
        } catch (error) {
            toast.error('Erro ao concluir transferência')
        }
    }

    const handleCancel = async (id: number) => {
        try {
            const response = await cancelTransfer(id)
            if (response.success) {
                toast.success('Transferência cancelada')
                loadTransfers()
            } else {
                toast.error(response.error || 'Erro ao cancelar transferência')
            }
        } catch (error) {
            toast.error('Erro ao cancelar transferência')
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando transferências...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg">
                        <MdSwapHoriz className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Transferências
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie transferências de pacientes
                        </p>
                    </div>
                </div>
                <button
                    className="btn btn-secondary gap-2"
                    onClick={handleOpenModal}
                >
                    <MdAdd className="text-xl" />
                    <span className="hidden sm:inline">Solicitar</span>
                </button>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
            </div>

            {transfers.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdSwapHoriz className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma transferência
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Nenhuma transferência solicitada
                        </p>
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Paciente</th>
                                <th>Tipo</th>
                                <th>Origem</th>
                                <th>Destino</th>
                                <th>Status</th>
                                <th>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transfers.map(transfer => (
                                <tr key={transfer.id}>
                                    <td>
                                        <div className="font-bold">
                                            {transfer.admission?.client.name}
                                        </div>
                                    </td>
                                    <td>
                                        <span className="badge badge-ghost">
                                            {
                                                TRANSFER_TYPE_LABELS[
                                                    transfer.type
                                                ]
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            {transfer.fromUnit?.name}
                                            {transfer.fromBed && (
                                                <span className="text-base-content/60">
                                                    {' '}
                                                    - Leito{' '}
                                                    {transfer.fromBed.number}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <div className="text-sm">
                                            {transfer.toUnit?.name ||
                                                transfer.externalFacility ||
                                                '-'}
                                            {transfer.toBed && (
                                                <span className="text-base-content/60">
                                                    {' '}
                                                    - Leito{' '}
                                                    {transfer.toBed.number}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <span
                                            className={`badge ${TRANSFER_STATUS_COLORS[transfer.status]}`}
                                        >
                                            {
                                                TRANSFER_STATUS_LABELS[
                                                    transfer.status
                                                ]
                                            }
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex gap-1">
                                            {transfer.status ===
                                                'TRANSF_REQUESTED' && (
                                                <>
                                                    <button
                                                        className="btn btn-success btn-sm gap-1"
                                                        onClick={() =>
                                                            handleAuthorize(
                                                                transfer.id,
                                                                true
                                                            )
                                                        }
                                                    >
                                                        <MdCheck />
                                                    </button>
                                                    <button
                                                        className="btn btn-error btn-sm gap-1"
                                                        onClick={() =>
                                                            handleAuthorize(
                                                                transfer.id,
                                                                false
                                                            )
                                                        }
                                                    >
                                                        <MdClose />
                                                    </button>
                                                </>
                                            )}
                                            {transfer.status ===
                                                'TRANSF_AUTHORIZED' && (
                                                <button
                                                    className="btn btn-secondary btn-sm gap-1"
                                                    onClick={() =>
                                                        handleStart(transfer.id)
                                                    }
                                                >
                                                    <MdPlayArrow />
                                                    Iniciar
                                                </button>
                                            )}
                                            {transfer.status ===
                                                'TRANSF_IN_PROGRESS' && (
                                                <button
                                                    className="btn btn-success btn-sm gap-1"
                                                    onClick={() =>
                                                        handleComplete(
                                                            transfer.id
                                                        )
                                                    }
                                                >
                                                    <MdCheck />
                                                    Concluir
                                                </button>
                                            )}
                                            {transfer.status !==
                                                'TRANSF_COMPLETED' &&
                                                transfer.status !==
                                                    'TRANSF_CANCELLED' && (
                                                    <button
                                                        className="btn btn-ghost btn-sm text-error"
                                                        onClick={() =>
                                                            handleCancel(
                                                                transfer.id
                                                            )
                                                        }
                                                    >
                                                        Cancelar
                                                    </button>
                                                )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* New Transfer Modal */}
            <dialog className={`modal ${showNewModal ? 'modal-open' : ''}`}>
                <div className="modal-box !max-w-md">
                    <h3 className="font-bold text-lg mb-4">
                        Solicitar Transferência
                    </h3>
                    <div className="space-y-4">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Internação{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <div className="relative">
                                <div className="relative">
                                    <input
                                        type="text"
                                        className="input input-bordered w-full pr-10"
                                        placeholder="Buscar por paciente, unidade ou ID..."
                                        value={searchTerm}
                                        onChange={e => {
                                            setSearchTerm(e.target.value)
                                            setShowAdmissionDropdown(true)
                                        }}
                                        onFocus={() =>
                                            setShowAdmissionDropdown(true)
                                        }
                                    />
                                    <MdSearch className="absolute z-10 right-3 top-1/2 -translate-y-1/2 text-base-content/40 text-xl" />
                                </div>

                                {showAdmissionDropdown && (
                                    <div className="absolute z-50 w-full mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                        {loadingAdmissions ? (
                                            <div className="p-4 text-center">
                                                <span className="loading loading-spinner loading-sm text-secondary"></span>
                                                <p className="text-sm text-base-content/60 mt-2">
                                                    Carregando internações...
                                                </p>
                                            </div>
                                        ) : filteredAdmissions.length === 0 ? (
                                            <div className="p-4 text-center text-sm text-base-content/60">
                                                {searchTerm
                                                    ? 'Nenhuma internação encontrada'
                                                    : 'Digite para buscar internações'}
                                            </div>
                                        ) : (
                                            filteredAdmissions.map(
                                                admission => (
                                                    <button
                                                        key={admission.id}
                                                        type="button"
                                                        onClick={() =>
                                                            handleSelectAdmission(
                                                                admission
                                                            )
                                                        }
                                                        className="w-full text-left p-3 hover:bg-base-200 transition-colors border-b border-base-300/50 last:border-b-0"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-shrink-0 mt-1">
                                                                <MdPerson className="text-lg text-secondary" />
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="font-medium text-base-content">
                                                                    {admission
                                                                        .client
                                                                        ?.name ||
                                                                        'Paciente não informado'}
                                                                </div>
                                                                <div className="flex items-center gap-2 mt-1 text-xs text-base-content/60">
                                                                    <div className="flex items-center gap-1">
                                                                        <MdLocalHospital className="text-sm" />
                                                                        <span>
                                                                            {admission
                                                                                .unit
                                                                                ?.name ||
                                                                                'Unidade não informada'}
                                                                        </span>
                                                                    </div>
                                                                    {admission.bed && (
                                                                        <span>
                                                                            •
                                                                            Leito{' '}
                                                                            {
                                                                                admission
                                                                                    .bed
                                                                                    .number
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="text-xs text-base-content/50 mt-1">
                                                                    ID:{' '}
                                                                    {
                                                                        admission.id
                                                                    }{' '}
                                                                    • Nº:{' '}
                                                                    {
                                                                        admission.admissionNumber
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </button>
                                                )
                                            )
                                        )}
                                    </div>
                                )}
                            </div>
                            {newTransfer.admissionId > 0 && (
                                <label className="label pt-1">
                                    <span className="label-text-alt text-success">
                                        Internação selecionada: ID{' '}
                                        {newTransfer.admissionId}
                                    </span>
                                </label>
                            )}
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Tipo *
                                </span>
                            </label>
                            <select
                                className="select select-bordered"
                                value={newTransfer.type}
                                onChange={e =>
                                    setNewTransfer({
                                        ...newTransfer,
                                        type: e.target.value as TransferType,
                                    })
                                }
                            >
                                {Object.entries(TRANSFER_TYPE_LABELS).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Motivo *
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered"
                                placeholder="Motivo da transferência..."
                                value={newTransfer.reason}
                                onChange={e =>
                                    setNewTransfer({
                                        ...newTransfer,
                                        reason: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                    <div className="modal-action">
                        <button
                            className="btn btn-ghost"
                            onClick={handleCloseModal}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleRequestTransfer}
                            disabled={saving || !newTransfer.admissionId}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : (
                                'Solicitar'
                            )}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={handleCloseModal}>close</button>
                </form>
            </dialog>
        </div>
    )
}
